/**
 * `?overrideTheme=...` forces a theme regardless of the tenant's default. The backend
 * (`index.html`) only reads it from `request.GET` on full-page loads, and SPA navigation
 * strips it from the URL — so copied/shared links would otherwise drop the override.
 *
 * The value is captured once at bootstrap (before any client-side navigation can strip it)
 * and read back from this cache by the share-url builder. It is intentionally NOT derived
 * from `settings.app.theme`: a genuine fieldview tenant has that theme without any override
 * param, and we must not stamp the param onto their URLs.
 */
const OVERRIDE_THEME_PARAM = 'overrideTheme';

let cachedOverrideTheme: string | null = null;

/** Capture the override param from the current URL. Call once at startup, before navigation. */
export const captureOverrideTheme = (): void => {
    cachedOverrideTheme = new URLSearchParams(window.location.search).get(OVERRIDE_THEME_PARAM);
};

/** Append `overrideTheme` to a URL when one was captured, preserving existing params. */
export const withOverrideTheme = (url: string): string => {
    if (!cachedOverrideTheme) return url;
    const [base, hash = ''] = url.split('#');
    const [path, query = ''] = base.split('?');
    const params = new URLSearchParams(query);
    params.set(OVERRIDE_THEME_PARAM, cachedOverrideTheme);
    return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
};
